import React, { useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ErrorInfo } from '#shared/types/common';
import Typography from '#web-components/components/Typography';
import Button from '#web-components/components/Button';
import FlashMessage, { ViewType } from '#web-components/components/FlashMessage';
import { SystemErrorDescription, EmailSupportError } from '#web-components/components/Layouts/Error';
import { NotificationErrorIcon as FireIcon } from '#web-components/components/Icons';
import styles from './CriticalError.styles';

const useStyles = makeStyles(styles, { name: 'CriticalError' });

interface CriticalErrorProps {
  error: ErrorInfo
}

export default function CriticalError({ error }: CriticalErrorProps) {
  const classes = useStyles();
  const { t } = useTranslation('components');

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  const errorFields = useMemo(() => {
    const errorInfoFields = [{
      name: t('errorLayout.pageUrl'),
      value: window.location.href,
    }];
    if (error.traceId) {
      errorInfoFields.unshift({
        name: t('errorLayout.traceId'),
        value: error.traceId,
      });
    }
    return errorInfoFields;
  }, [error.traceId, t]);

  return (
    <div className={classes.root}>
      <FireIcon variant="h1" />
      <Typography variant="h4" className={classes.title}>
        {t('errorLayout.smthWentWrong')}
      </Typography>
      <Typography variant="textRegular" className={classes.message}>
        {t('errorLayout.reloadPageAndContactAdministrator')}
      </Typography>
      <FlashMessage
        viewType={ViewType.plain}
        status="service"
        size="small"
        title={t('errorLayout.systemErrorDescription')}
        xpathConfig={{ titleBlock: 'errorLayout.systemErrorDescription' }}
        message={(
          <SystemErrorDescription
            fields={errorFields}
          />
        )}
      />
      <FlashMessage
        viewType={ViewType.plain}
        status="warning"
        size="small"
        title={t('errorLayout.needHelp')}
        className={classes.supportEmail}
        message={(
          <EmailSupportError
            description={t('errorLayout.copySystemErrorDescription')}
            mail={{
              email: REGISTRY_ENVIRONMENT_VARIABLES.supportEmail,
              subject: t('errors:supportEmail.subject'),
              body: t('errors:supportEmail.body'),
            }}
            errorData={errorFields}
          />
        )}
      />
      <Button onClick={reloadPage} size="large" className={classes.button}>
        {t('errorLayout.reloadPage')}
      </Button>
    </div>
  );
}
